import { JSX, useRef, useState } from "react"
import "./dialogsystem.scss"
import { CurrentDialogContext, DialogControllerContext, useCurrentDialog, useDialogController } from "../GlobalData"
import M3ripple from "../common/components/m3ripple";

type Dialog = {
    renderer: () => JSX.Element,
    buttons: [string, (close: () => void) => void][],
    closeAction: () => void,
    enableClose: boolean,
    title: string
}

function generateInsecureUUID(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        const r = Math.random() * 16 | 0;
        // For the 'y' position, the value must be 8, 9, A, or B (binary 10xx)
        // to conform to the UUID standard.
        const v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);

    });
}

export function DialogHost({ children }: { children: JSX.Element | JSX.Element[] }) {
    const [dialogs, setDialogs] = useState({
    } as Record<string, Dialog>)
    const permRef = useRef({} as Record<string, Dialog>)
    const value: ReturnType<typeof useDialogController> = {
        showDialog: (content, buttons, enableClose, title) => {
            const id = generateInsecureUUID();
            const closeAction = () => {
                const cloned: Record<string, Dialog> = {
                    ...permRef.current,
                }
                delete cloned[id]
                setDialogs(cloned);
                permRef.current = cloned;
            }
            const modified: Record<string, Dialog> = {
                ...permRef.current,
                [id]: {
                    renderer: content,
                    buttons,
                    closeAction,
                    enableClose,
                    title
                }
            }
            setDialogs(modified);
            permRef.current = modified;
            return id;
        },
        hideDialog: (id) => {
            const cloned: Record<string, Dialog> = {
                ...permRef.current,
            }
            delete cloned[id]
            setDialogs(cloned);
            permRef.current = cloned;
        },
        showDialogAsync: (content, buttons, enableClose, title) => {
            const id = generateInsecureUUID();
            let resolver: (buttonid: string) => void = () => { }
            const promise = new Promise<string>((resolve) => {
                resolver = resolve;
            })
            const closeAction = () => {
                const cloned: Record<string, Dialog> = {
                    ...permRef.current,
                }
                delete cloned[id]
                setDialogs(cloned);
                permRef.current = cloned;
            }
            const modified: Record<string, Dialog> = {
                ...permRef.current,
                [id]: {
                    renderer: content,
                    buttons: buttons.map((x) => {
                        return [x, () => {
                            closeAction()
                            resolver(x)
                        }]
                    }),
                    enableClose,
                    closeAction: () => {
                        closeAction()
                        resolver("close")
                    },
                    title
                }
            }
            setDialogs(modified);
            permRef.current = modified;
            return promise;
        }
    }
    const visible = Object.keys(dialogs).length > 0;
    const currentDialog = Object.keys(dialogs)[0];

    const currentDialogVal: ReturnType<typeof useCurrentDialog> = {
        hideDialog: visible ? () => value.hideDialog(currentDialog) : () => { }
    }
    return <DialogControllerContext.Provider value={value}>
        <CurrentDialogContext.Provider value={currentDialogVal}>
            <div className={`w-[100dvw] h-[100dvh] fixed top-0 left-0 dialoghost flex items-center justify-center ${visible ? "dialoghostvisible" : "dialoghosthidden"}`}>
                <div className="w-[90%] h-max bg-white rounded-2xl inline-flex flex-col justify-start items-start overflow-hidden">
                    <div className="self-stretch p-2.5 outline outline-1 outline-offset-[-1px] outline-zinc-100 inline-flex justify-start items-center gap-2.5 overflow-hidden">
                        <button data-svg-wrapper className="relative rounded-full" onClick={visible ? dialogs[currentDialog].closeAction : () => { }}>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M6.4 19L5 17.6L10.6 12L5 6.4L6.4 5L12 10.6L17.6 5L19 6.4L13.4 12L19 17.6L17.6 19L12 13.4L6.4 19Z" fill="black" />
                            </svg>
                            <M3ripple />
                        </button>
                        <div className="flex-1 flex justify-center items-center gap-2.5">
                            <div className="justify-start text-black text-sm font-semibold font-['Be_Vietnam_Pro'] leading-tight">{visible ? dialogs[currentDialog].title : <></>}</div>
                        </div>
                    </div>
                    <div className="self-stretch flex-1 px-4 py-2.5 rounded-2xl flex flex-col justify-start items-start gap-2.5 overflow-hidden">
                        <div className="self-stretch flex-1">
                            {visible ? dialogs[currentDialog].renderer() : <></>}
                        </div>
                        <div className="self-stretch inline-flex justify-end items-start gap-2.5 overflow-hidden">

                            {visible ? dialogs[currentDialog].buttons.map(x => <button key={x[0]} aria-label={`Dialog box ${x[0]}`} className="relative" onClick={() => {
                                x[1](() => {
                                    const cloned: Record<string, Dialog> = {
                                        ...permRef.current,
                                    }
                                    delete cloned[x[0]]
                                    setDialogs(cloned);
                                    permRef.current = cloned;
                                })
                            }}>
                                <div className="size- px-3.5 py-3 bg-neutral-100 rounded-md outline outline-1 outline-offset-[-1px] outline-teal-900 flex justify-start items-start gap-1 ">
                                    <div className="justify-start text-zinc-600 text-lg font-normal font-['Be_Vietnam_Pro']">{x[0]}</div>
                                    <M3ripple />
                                </div>
                            </button>) : <></>}
                        </div>
                    </div>
                </div>
            </div>
        </CurrentDialogContext.Provider>
        {children}
    </DialogControllerContext.Provider>
}